import { Link, useParams } from 'react-router';

import { ROUTES } from '#/config/routes';
import { fetchStockReceiptDetail } from '#/api/imsMgmt/receipts/stockReceipt/fetchDetail';
import { updateStockReceiptRemark } from '#/api/imsMgmt/receipts/stockReceipt/updateRemark';
import { Button } from '#/modules/commonComponents/button';
import PageShell from '#/modules/infra/layout/PageShell';
import useDocumentRemarkSave from '#/hooks/useDocumentRemarkSave';
import DocumentRemarkEditForm from '#/modules/imsMgmt/receipts/shared/DocumentRemarkEditForm';
import { canEditStockReceiptRemark } from '#/modules/imsMgmt/receipts/shared/receiptStatus';
import StockReceiptMainInfoCard from '#/modules/imsMgmt/receipts/stockReceipt/components/StockReceiptMainInfoCard';
import {
  getStockReceiptApiErrorMessage,
  isStockReceiptVersionConflict,
} from '#/modules/imsMgmt/receipts/stockReceipt/helper/apiError';
import {
  stockReceiptDetailQueryKey,
  stockReceiptListQueryKeyPrefix,
} from '#/utils/queryKeys';

/**
 * 入庫備註專頁。轉倉／調整可複製此組裝，換 API／MainInfoCard 即可。
 */
const StockReceiptRemarkPage = () => {
  const { id } = useParams();

  const {
    detail,
    detailQuery,
    isPending,
    saveRemark,
  } = useDocumentRemarkSave({
    id,
    fetchDetail: fetchStockReceiptDetail,
    updateRemark: updateStockReceiptRemark,
    detailQueryKey: stockReceiptDetailQueryKey,
    listQueryKeyPrefix: stockReceiptListQueryKeyPrefix,
    getErrorMessage: (error) => getStockReceiptApiErrorMessage(error, '更新備註失敗'),
    isVersionConflict: isStockReceiptVersionConflict,
    afterSaveNavigateTo: ROUTES.stockReceiptDetail(id),
  });

  const isLoading = detailQuery.isLoading;
  const isError = detailQuery.isError;
  const remarkAllowed = detail ? canEditStockReceiptRemark(detail.status) : false;

  return (
    <PageShell variant="form">
      <div className="layout-shell__body">
        <div className="mx-auto w-full max-w-4xl space-y-4">
          <div className="create_edit_page__header">
            <h1 className="page_title mb-0!">
              <span className="border-l-5 border-primary-400 pl-2.5">編輯入庫備註</span>
            </h1>
            <Button type="button" variant="outline" asChild>
              <Link to={ROUTES.stockReceiptDetail(id)}>返回詳情</Link>
            </Button>
          </div>

          {isLoading ? (
            <p className="text-sm text-slate-500">載入中…</p>
          ) : null}

          {isError ? (
            <p className="text-sm text-destructive">
              {getStockReceiptApiErrorMessage(detailQuery.error, '找不到入庫單')}
            </p>
          ) : null}

          {detail && !remarkAllowed ? (
            <div className="space-y-3">
              <p className="text-sm text-slate-600">此狀態不可編輯備註。</p>
              <Button type="button" asChild>
                <Link to={ROUTES.stockReceiptDetail(id)}>前往詳情</Link>
              </Button>
            </div>
          ) : null}

          {detail && remarkAllowed ? (
            <>
              <StockReceiptMainInfoCard detail={detail} />
              <DocumentRemarkEditForm
                initialRemark={detail.remark ?? ''}
                cancelTo={ROUTES.stockReceiptDetail(id)}
                isPending={isPending}
                onSave={saveRemark}
              />
            </>
          ) : null}
        </div>
      </div>
    </PageShell>
  );
};

export default StockReceiptRemarkPage;
